import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddEditCustomLocationModal, type AddEditCustomLocationSaveData } from '@/components/modals/AddEditCustomLocationModal';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

import { getAvailableSubmodules } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const getSubmoduleOrKerktonRoleFromLogEntry = _wg('getSubmoduleOrKerktonRoleFromLogEntry');

const locationColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'Id', size: 'hidden' },
  {
    field: 'location',
    name: 'Location',
    size: 'col-xs-3',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.location)) {
        return e.location;
      }
      return 'Error getting location.';
    },
  },
  {
    field: 'coordinates',
    name: 'Coordinates',
    size: 'col-xs-7',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.latitude) && hasValue(e.longitude)) {
        return '' + e.latitude + ',' + e.longitude;
      }
      return 'Error getting coordinates.';
    },
  },
];

const auditLogColumns: KstDataGridColumn[] = [
  {
    field: 'dateCreated',
    name: 'Date / Time',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.dateCreated)) {
        return moment(e.dateCreated).format('MM/DD/YYYY HH:mm:ss');
      }
      return 'Error getting date / time.';
    },
  },
  {
    field: 'loggedByFirstName',
    name: 'First',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.loggedByFirstName) ? e.loggedByFirstName : ''),
  },
  {
    field: 'loggedByLastName',
    name: 'Last',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.loggedByLastName) ? e.loggedByLastName : ''),
  },
  {
    field: 'role',
    name: 'Role',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && hasValue(e.role) ? getSubmoduleOrKerktonRoleFromLogEntry(e) : ''),
  },
  {
    field: 'action',
    name: 'Action',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && hasValue(e.action) ? e.action : ''),
  },
  {
    field: 'entry',
    name: 'Entry',
    size: 'col-xs-4',
    template: (e: any) => (hasValue(e) && hasValue(e.entry) ? e.entry : ''),
  },
];

function getHeaderButtons(): Array<{ name: string; function: string; src: string }> {
  const arr: Array<{ name: string; function: string; src: string }> = [];
  let submodules: string[] | null = getAvailableSubmodules();
  if (submodules === null) {
    submodules = [];
  }
  if (isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_SAVE)) {
    arr.push({ name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' });
  }
  if (isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_UPDATE)) {
    arr.push({ name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' });
  }
  if (isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_DELETE)) {
    arr.push({ name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' });
  }
  return arr;
}

function hasAuditLogPermissions(): boolean {
  let submodules: string[] | null = getAvailableSubmodules();
  if (submodules === null) {
    return false;
  }
  return isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_AUDIT_LOG);
}

function hideCheckboxes(): boolean {
  let submodules: string[] | null = getAvailableSubmodules();
  if (submodules === null) {
    return true;
  }
  return (
    !isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_UPDATE) &&
    !isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_DELETE)
  );
}

export function CustomLocationManagementScreen() {
  const { theme } = useTheme();

  const hasAuditLog = useMemo(() => hasAuditLogPermissions(), []);
  const shouldHideCheckboxes = useMemo(() => hideCheckboxes(), []);
  const headerButtons = useMemo(() => getHeaderButtons(), []);

  const mobileTabs = useMemo(() => {
    const t = [{ id: 'showManageCustomLocationTab', label: 'Locations' }];
    if (hasAuditLog) {
      t.push({ id: 'showCustomLocationAuditLogTab', label: 'Audit Log' });
    }
    return t;
  }, [hasAuditLog]);

  const [activeTab, setActiveTab] = useState('showManageCustomLocationTab');
  const [activePage, setActivePage] = useState('showManageCustomLocation');

  // Location entries
  const [locationEntries, setLocationEntries] = useState<any[] | null>(null);

  // Loading states
  const [isLocationsLoading, setIsLocationsLoading] = useState<boolean>(false);
  const [isAuditLogLoading, setIsAuditLogLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Audit log entries
  const [auditLogEntries, setAuditLogEntries] = useState<any[]>([]);
  const [auditLogPager, setAuditLogPager] = useState<KstDataGridPager>({
    curPage: undefined,
    numPages: undefined,
    records: undefined,
    isVisible: false,
  });

  // Caches
  const locationListCacheRef = useRef<any | null>(null);
  const auditLogPageRef = useRef<number | null>(null);
  const auditLogListCacheRef = useRef<any | null>(null);

  // Add/Edit modal
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [addEditMode, setAddEditMode] = useState<'add' | 'edit'>('add');
  const [addEditCustomLocation, setAddEditCustomLocation] = useState<any>({});

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Grid ref
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const locationRows: KstDataGridRows = useMemo(() => ({ entries: locationEntries }), [locationEntries]);
  const auditLogRowsData: KstDataGridRows = useMemo(() => ({ entries: auditLogEntries }), [auditLogEntries]);

  // Cache helpers
  const getCustomLocationList = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (hasValue(locationListCacheRef.current)) {
        resolve(deepCopyProperties(locationListCacheRef.current));
      } else {
        api.listCustomLocations(
          true,
          (result: any) => {
            locationListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting custom locations.', 'error');
          }
        );
      }
    });
  }, []);

  const clearCaches = useCallback(() => {
    locationListCacheRef.current = null;
  }, []);

  const clearAuditLogCaches = useCallback(() => {
    auditLogPageRef.current = null;
    auditLogListCacheRef.current = null;
  }, []);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    }, 0);
  }, []);

  const refreshManageCustomLocationList = useCallback(
    (doScrollToBottom = false) => {
      setIsLocationsLoading(true);
      getCustomLocationList().then(
        (listResponse: { locations: Array<{ id: number; location: string; latitude: number; longitude: number }> }) => {
          listResponse.locations.sort((a, b) => a.location.localeCompare(b.location));
          setLocationEntries(listResponse.locations);
          setIsLocationsLoading(false);

          if (doScrollToBottom) {
            scrollToBottom();
          }
        }
      ).catch(() => {
        setIsLocationsLoading(false);
      });
    },
    [getCustomLocationList, scrollToBottom]
  );

  const getAuditLogPage = useCallback(
    (page: number) => {
      const cached =
        hasValue(auditLogPageRef.current) &&
        hasValue(auditLogListCacheRef.current) &&
        auditLogPageRef.current === page;

      const doUpdate = (response: any) => {
        const entries: any[] = [];
        for (let i = 0; i < response.logs.length; i++) {
          const entry = response.logs[i];
          entry.isChecked = false;
          entries.push(entry);
        }
        setAuditLogEntries(entries);
        setAuditLogPager({
          curPage: response.curPage,
          numPages: response.numPages,
          records: response.records,
          isVisible: true,
        });
        setIsAuditLogLoading(false);
      };

      setIsAuditLogLoading(true);
      if (cached) {
        doUpdate(deepCopyProperties(auditLogListCacheRef.current));
      } else {
        api.getCustomLocationAuditLogEntries(
          { data: { page, pageSize: 30 } },
          (result: any) => {
            auditLogPageRef.current = page;
            auditLogListCacheRef.current = result;
            doUpdate(deepCopyProperties(result));
          },
          () => {
            App.showPopupNotification('Error getting custom location audit log.', 'error');
            setIsAuditLogLoading(false);
          }
        );
      }
    },
    []
  );

  // Load initial data
  useEffect(() => {
    refreshManageCustomLocationList();
  }, []);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'showManageCustomLocationTab') {
        setActivePage('showManageCustomLocation');
      } else if (tabId === 'showCustomLocationAuditLogTab') {
        setActivePage('showCustomLocationAuditLog');
        getAuditLogPage(1);
      }
    },
    [getAuditLogPage]
  );

  const handleButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'add') {
        setAddEditMode('add');
        setAddEditCustomLocation({});
        setAddEditModalOpen(true);
      } else if (functionName === 'edit') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Please select the custom location to edit by clicking the checkbox.', 'warning');
          return;
        } else if (checkedItems.length > 1) {
          App.showPopupNotification('Only one custom location may be edited at a time.', 'warning');
          return;
        }
        const item = checkedItems[0];
        setAddEditMode('edit');
        setAddEditCustomLocation({
          id: item.id,
          location: item.location,
          latitude: item.latitude,
          longitude: item.longitude,
        });
        setAddEditModalOpen(true);
      } else if (functionName === 'delete') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Custom Location must be selected to delete.', 'warning');
          return;
        }
        const count = checkedItems.length;
        const message =
          count === 1
            ? 'Are you sure you want to delete this custom location?'
            : `Are you sure you want to delete ${count} custom locations?`;
        setDeleteConfirmItems(checkedItems);
        setDeleteConfirmMessage(message);
        setDeleteConfirmOpen(true);
      }
    },
    []
  );

  const handleAddEditSave = useCallback(
    (data: AddEditCustomLocationSaveData) => {
      const customLocationData: any = {
        location: data.location,
        latitude: data.latitude,
        longitude: data.longitude,
      };

      setIsSaving(true);
      if (addEditMode === 'add') {
        api.saveCustomLocation(
          customLocationData,
          () => {
            setIsSaving(false);
            clearCaches();
            clearAuditLogCaches();
            refreshManageCustomLocationList(true);
            setAddEditModalOpen(false);
          },
          () => {
            setIsSaving(false);
            App.showPopupNotification('Failed to save custom location.', 'error');
          }
        );
      } else {
        customLocationData.id = data.id;
        api.updateCustomLocation(
          customLocationData,
          () => {
            setIsSaving(false);
            clearCaches();
            clearAuditLogCaches();
            refreshManageCustomLocationList(true);
            setAddEditModalOpen(false);
          },
          () => {
            setIsSaving(false);
            App.showPopupNotification('Failed to update custom location.', 'error');
          }
        );
      }
    },
    [addEditMode, clearCaches, clearAuditLogCaches, refreshManageCustomLocationList]
  );

  const handleConfirmDelete = useCallback(() => {
    const idList = deleteConfirmItems.map((elt: any) => elt.id);
    setIsDeleting(true);
    api.deleteCustomLocations(
      { idList },
      () => {
        setIsDeleting(false);
        clearCaches();
        clearAuditLogCaches();
        refreshManageCustomLocationList(true);
        setDeleteConfirmOpen(false);
        setDeleteConfirmItems([]);
        setDeleteConfirmMessage('');
      },
      () => {
        setIsDeleting(false);
        App.showPopupNotification('Failed to delete custom locations.', 'error');
      }
    );
  }, [deleteConfirmItems, clearCaches, clearAuditLogCaches, refreshManageCustomLocationList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  return (
    <div className={theme}>
      <AddEditCustomLocationModal
        open={addEditModalOpen}
        mode={addEditMode}
        customLocation={addEditCustomLocation}
        theme={theme}
        saving={isSaving}
        onSave={handleAddEditSave}
        onCancel={() => setAddEditModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Custom Location"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={mobileTabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showManageCustomLocationTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showManageCustomLocationTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span className="kst-sidebar-text">Locations</span>
              </li>
              {hasAuditLog && (
                <li
                  className={`kst-sidebar-item${activeTab === 'showCustomLocationAuditLogTab' ? ' kst-sidebar-item--active' : ''}`}
                  onClick={() => handleTabChange('showCustomLocationAuditLogTab')}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="kst-sidebar-text">Audit Log</span>
                </li>
              )}
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Custom Location Management Page */}
            {activePage === 'showManageCustomLocation' && (
              <div className="kst-page" ref={gridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Custom Location Management</h3>
                  <p className="kst-page-description">
                    Associate a custom location with coordinates so that you can use the custom location instead of the coordinates for locations in Orna.
                  </p>
                </div>

                <KstDataGrid
                  columns={locationColumns}
                  rows={locationRows}
                  headerbuttons={headerButtons}
                  showcheckbox={!shouldHideCheckboxes}
                  mobiletitlefield="location"
                  loading={isLocationsLoading}
                  onButtonCommand={handleButtonCommand}
                />
              </div>
            )}

            {/* Audit Log Page */}
            {activePage === 'showCustomLocationAuditLog' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Custom Location Audit Log</h3>
                </div>

                <KstDataGrid
                  columns={auditLogColumns}
                  rows={auditLogRowsData}
                  pager={auditLogPager}
                  mobiletitlefield="entry"
                  showcheckbox={false}
                  showheader={false}
                  loading={isAuditLogLoading}
                  onGoToPage={getAuditLogPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomLocationManagementScreen;

